// Reading an attendance log exported from a device (CSV, Excel, or the device's own tab-separated .dat).
// Devices agree on almost nothing: column order, headers, date order, 12 or 24 hour clocks. So the person picks the
// columns and the date order once, and this turns each line into { device_user, punched_local, direction }.
// Everything is text: dates are never run through Date() (a timezone shift would move the day).

const pad = (n) => String(n).padStart(2, "0");
const validDate = (y, m, d) => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
};

export const DATE_ORDERS = [
  { value: "DMY", label: "Day / Month / Year (05/10/2026)" },
  { value: "MDY", label: "Month / Day / Year (10/05/2026)" },
  { value: "YMD", label: "Year / Month / Day (2026-10-05)" },
];

// A spreadsheet date number (days since 1900, the fraction is the time of day) -> "YYYY-MM-DD HH:MM:SS".
export function fromSerial(n) {
  if (!Number.isFinite(n) || n < 1) return null;
  return new Date(Math.round((n - 25569) * 86400) * 1000).toISOString().slice(0, 19).replace("T", " ");
}

// "05/10/2026 8:01 PM" -> "2026-10-05 20:01:00". `order` says how a day-first or month-first date reads; a year-first date is
// always understood. null when it cannot be read.
export function parseDateTime(value, order = "DMY") {
  if (typeof value === "number") return fromSerial(value);
  const text = String(value ?? "").trim();
  if (!text) return null;
  const m = /^(\d{1,4})[-/.](\d{1,2})[-/.](\d{1,4})[ T,]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/.exec(text);
  if (!m) return null;
  let [, a, b, c] = m;
  let y;
  let mo;
  let d;
  if (a.length === 4) {
    y = Number(a);
    mo = Number(b);
    d = Number(c);
  } else if (c.length === 4) {
    y = Number(c);
    if (order === "MDY") {
      mo = Number(a);
      d = Number(b);
    } else {
      d = Number(a);
      mo = Number(b);
    }
  } else return null;
  let h = Number(m[4]);
  const mi = Number(m[5]);
  const s = Number(m[6] || 0);
  const ap = m[7] && m[7].toLowerCase();
  if (ap) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (ap === "pm" ? 12 : 0);
  }
  if (!validDate(y, mo, d) || h > 23 || mi > 59 || s > 59) return null;
  return `${y}-${pad(mo)}-${pad(d)} ${pad(h)}:${pad(mi)}:${pad(s)}`;
}

// A separate date column and time column -> one text the parser reads.
export function joinDateAndTime(date, time) {
  if (typeof date === "number") date = fromSerial(Math.floor(date))?.slice(0, 10);
  let t = time;
  if (typeof time === "number") t = new Date(Math.round(time * 86400) * 1000).toISOString().slice(11, 19);
  return `${String(date ?? "").trim()} ${String(t ?? "").trim()}`;
}

const looksLikeHeader = (row) => row.some((c) => typeof c === "string" && /[A-Za-z]/.test(c) && !parseDateTime(c));

// Splits the device's text file: tab, comma, semicolon or pipe, whichever the first line uses most.
export function splitText(text) {
  const lines = String(text).split(/\r?\n/).filter((l) => l.trim() !== "");
  if (!lines.length) return [];
  const delim = ["\t", ",", ";", "|"].map((d) => [d, (lines[0].match(new RegExp(d === "|" ? "\\|" : d, "g")) || []).length]).sort((a, b) => b[1] - a[1])[0];
  const d = delim[1] ? delim[0] : /\s{2,}/.test(lines[0]) ? /\s{2,}/ : /\s+/;
  return lines.map((l) => l.split(d).map((c) => c.trim().replace(/^"|"$/g, "")));
}

const NAMES = {
  user: /^(user\s*(id|no|number)?|person\s*id|emp(loyee)?\s*(id|no|code)?|enroll(ment)?\s*(id|no)?|badge|card\s*no|id|no\.?)$/i,
  datetime: /^(date\s*[/&-]?\s*time|time|datetime|timestamp|check\s*time|punch\s*time|clock\s*time)$/i,
  date: /^(date|day)$/i,
  clock: /^(time|clock)$/i,
  direction: /^(status|state|in\s*\/?\s*out|direction|punch|type|check\s*(in|type)|verify\s*type)$/i,
};

// A first guess at what each column is, so most files need no changes: { hasHeader, user, datetime, date, time, direction: null, directionColumn? }.
export function guessColumns(rows) {
  const first = rows[0] || [];
  const hasHeader = looksLikeHeader(first);
  const out = { hasHeader, user: 0, datetime: 1, date: null, time: null, direction: null };
  if (hasHeader) {
    const find = (re) => first.findIndex((h) => re.test(String(h).trim()));
    const user = find(NAMES.user);
    const dt = find(NAMES.datetime);
    const date = find(NAMES.date);
    const clock = first.findIndex((h) => NAMES.clock.test(String(h).trim()));
    if (user >= 0) out.user = user;
    // "Date" beside "Time" means two columns; a lone "Time" (or "Date/Time") is the whole stamp
    if (date >= 0 && clock >= 0) Object.assign(out, { datetime: null, date, time: clock });
    else if (dt >= 0) out.datetime = dt;
    // The in/out column is offered but never chosen for the person: devices often get it wrong, and pairing in order is safer.
    const dir = find(NAMES.direction);
    if (dir >= 0) out.directionColumn = dir;
  }
  return out;
}

const IN_WORDS = /^(in|c\/in|check\s*-?in|clock\s*-?in|0)$/i;
const OUT_WORDS = /^(out|c\/out|check\s*-?out|clock\s*-?out|1)$/i;
export const directionOf = (v) => {
  const t = String(v ?? "").trim();
  return IN_WORDS.test(t) ? "IN" : OUT_WORDS.test(t) ? "OUT" : "";
};

// mapping: { user, datetime | (date, time), direction | null, order, hasHeader }.
// -> the rows to send. A line whose time cannot be read is sent as it is so the server names it in its list of problems.
export function buildRows(aoa, mapping) {
  const body = mapping.hasHeader ? aoa.slice(1) : aoa;
  const rows = [];
  for (const line of body) {
    if (!line || line.every((c) => c === "" || c === null || c === undefined)) continue;
    const rawTime = mapping.datetime !== null && mapping.datetime !== undefined ? line[mapping.datetime] : joinDateAndTime(line[mapping.date], line[mapping.time]);
    const parsed = parseDateTime(rawTime, mapping.order);
    let user = line[mapping.user];
    if (typeof user === "number") user = String(user);
    rows.push({
      device_user: String(user ?? "").trim(),
      punched_local: parsed || String(rawTime ?? ""),
      ...(mapping.direction !== null && mapping.direction !== undefined && directionOf(line[mapping.direction]) ? { direction: directionOf(line[mapping.direction]) } : {}),
    });
  }
  return rows;
}
